import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import {
  LayoutDashboard, CalendarCheck, BedDouble, DoorOpen, ClipboardCheck,
  FileText, Settings as SettingsIcon, Users as UsersIcon, LogOut, Menu, Receipt, Sparkles, Star, Compass, Building2,
} from "lucide-react";
import { useState } from "react";

const nav = [
  { to: "/admin/dashboard", label: "Dashboard", icon: LayoutDashboard, roles: ["super_admin", "admin", "hotel_manager"] },
  { to: "/admin/bookings", label: "Bookings", icon: CalendarCheck, roles: ["super_admin", "admin", "hotel_manager", "receptionist"] },
  { to: "/admin/checkin", label: "Check-in", icon: ClipboardCheck, roles: ["super_admin", "admin", "hotel_manager", "receptionist"] },
  { to: "/admin/housekeeping", label: "Housekeeping", icon: Sparkles, roles: ["super_admin", "admin", "hotel_manager", "receptionist"] },
  { to: "/admin/room-types", label: "Room Types", icon: BedDouble, roles: ["super_admin", "admin"] },
  { to: "/admin/room-units", label: "Room Units", icon: DoorOpen, roles: ["super_admin", "admin", "hotel_manager"] },
  { to: "/admin/activities", label: "Activities", icon: Compass, roles: ["super_admin", "admin"] },
  { to: "/admin/services", label: "Services", icon: Building2, roles: ["super_admin", "admin"] },
  { to: "/admin/service-bookings", label: "Service Bookings", icon: CalendarCheck, roles: ["super_admin", "admin", "hotel_manager", "receptionist"] },
  { to: "/admin/quotes", label: "Custom Quotes", icon: FileText, roles: ["super_admin", "admin", "hotel_manager"] },
  { to: "/admin/reviews", label: "Reviews", icon: Star, roles: ["super_admin", "admin"] },
  { to: "/admin/expenses", label: "Expenses", icon: Receipt, roles: ["super_admin", "admin", "hotel_manager"] },
  { to: "/admin/reports", label: "Reports", icon: FileText, roles: ["super_admin", "admin", "hotel_manager"] },
  { to: "/admin/settings", label: "Settings", icon: SettingsIcon, roles: ["super_admin", "admin"] },
  { to: "/admin/users", label: "Team", icon: UsersIcon, roles: ["super_admin", "admin", "hotel_manager"] },
];

const roleLabel = {
  super_admin: "Owner",
  admin: "Admin",
  hotel_manager: "Manager",
  receptionist: "Reception",
};

const roleColor = {
  super_admin: "text-[color:var(--brand)]",
  admin: "text-[color:var(--deep)]",
  hotel_manager: "text-blue-700",
  receptionist: "text-amber-700",
};

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const handleLogout = async () => { await logout(); navigate("/admin/login"); };
  const items = nav.filter((n) => n.roles.includes(user?.role));

  return (
    <div className="min-h-screen bg-[color:var(--sand)] font-ui" data-testid="admin-layout">
      <header className="lg:hidden sticky top-0 z-30 bg-white border-b flex items-center justify-between px-4 py-3">
        <button onClick={() => setOpen((v) => !v)} className="p-2" data-testid="mobile-menu-toggle"><Menu className="w-5 h-5" /></button>
        <span className="font-heading text-lg text-[color:var(--deep)]">Baraka Inn</span>
        <Button onClick={handleLogout} variant="ghost" size="sm" data-testid="mobile-logout"><LogOut className="w-4 h-4" /></Button>
      </header>

      <div className="flex">
        <aside className={`fixed lg:sticky top-0 left-0 z-40 h-screen w-72 bg-white border-r border-[color:var(--brand)]/10 flex flex-col p-6 transition-transform ${open ? "translate-x-0" : "-translate-x-full"} lg:translate-x-0`}>
          <div className="flex items-center gap-2 pb-6 border-b border-[color:var(--brand)]/10">
            <div className="w-10 h-10 rounded-full bg-[color:var(--brand)] flex items-center justify-center text-white font-heading font-bold">B</div>
            <div>
              <p className="font-heading text-lg text-[color:var(--deep)] leading-tight">Baraka Inn</p>
              <p className="text-xs text-[color:var(--muted-text)]">Hotel Console</p>
            </div>
          </div>

          <nav className="flex-1 pt-4 space-y-1 overflow-y-auto">
            {items.map((n) => (
              <NavLink key={n.to} to={n.to} onClick={() => setOpen(false)}
                data-testid={`nav-${n.label.toLowerCase().replace(/\s/g, "-")}`}
                className={({ isActive }) => `flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm transition-colors ${isActive ? "bg-[color:var(--brand)] text-white shadow-sm" : "text-[color:var(--deep)] hover:bg-[color:var(--sand)]"}`}>
                <n.icon className="w-4 h-4" />{n.label}
              </NavLink>
            ))}
          </nav>

          <div className="pt-4 border-t border-[color:var(--brand)]/10">
            <div className="px-3 py-2">
              <p className="text-xs text-[color:var(--muted-text)] uppercase tracking-wider">Signed in as</p>
              <p className="text-sm font-medium text-[color:var(--deep)] truncate" data-testid="current-user-email">{user?.email}</p>
              <p className={`text-xs mt-0.5 font-semibold ${roleColor[user?.role] || ""}`}>{roleLabel[user?.role] || user?.role}</p>
            </div>
            <Button onClick={handleLogout} variant="outline" className="w-full mt-3 rounded-full" data-testid="logout-button">
              <LogOut className="w-4 h-4 mr-2" /> Sign out
            </Button>
          </div>
        </aside>

        {open && <div className="fixed inset-0 bg-black/30 z-30 lg:hidden" onClick={() => setOpen(false)} />}

        <main className="flex-1 min-w-0 p-4 sm:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
